<template>
  <v-skeleton-loader
    :loading="isLoading"
    transition="expand-transition"
    type="article"
  >
    <FormDriedAbaloneSummary
      :DriedAbaloneSummary="editDriedAbaloneSummary"
      :isSaving="isSaving"
      :SummaryItems="summaryItems"
      @save="save"
      @cancel="$router.push({name: from.name })" />
  </v-skeleton-loader>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from 'dayjs';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import FormDriedAbaloneSummary from '../components/FormDriedAbaloneSummary.vue';
import checklist from './checklist';
import formatting from '../mixins/formatting';
import stockItem from './stockItem';

export default {
  components: {
    FormDriedAbaloneSummary,
  },
  name: 'EditDriedAbaloneSummary',
  module: 'DriedAbaloneSummary',
  mixins: [checklist, formatting, loading, stockItem],
  data() {
    return {
      /**
      * Get/Set editing DriedAbaloneSummary. Default to {}.
      */
      editDriedAbaloneSummary: {},
      /**
       * Query Id for DriedAbaloneSummary
       */
      driedAbaloneSummaryId: this.$route?.query.id,
      summaryItems: [],
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('driedAbaloneSummaries', ['driedAbaloneSummaries', 'driedAbaloneSummary']),
    /** Get the mode for this view, create or update
     */
    mode() {
      if (this.driedAbaloneSummaryId) {
        return 'edit';
      }
      return 'add';
    },
  },
  async mounted() {
    this.$on('stockItem.loaded', async (item) => {
      if (this.mode === 'add') {
        this.isLoading = true;
        const stockItemLinks = await this.LOAD_stockItemLinksByParent(item.id);
        console.debug(stockItemLinks);
        const minSize = stockItemLinks
          .map((link) => link.childStockItem.sizeMin)
          .reduce((a, b) => Math.min(a, b));
        const maxSize = stockItemLinks
          .map((link) => link.childStockItem.sizeMin)
          .reduce((a, b) => Math.max(a, b));
        console.debug(minSize);
        const totalOutOfSalt = stockItemLinks
          .map((link) => link.childStockItem
            .processCheckSheets
            .reduce((total, sheet) => parseInt(sheet.totalOutOfSaltWeightDry ?? '0', 10)
                + parseInt(sheet.totalOutOfSaltWeightDry ?? '0', 10) + total));
        this.editDriedAbaloneSummary.drySizeRange = `${minSize} - ${maxSize}`;
        this.editDriedAbaloneSummary.outOfSaltWeight = totalOutOfSalt;
        this.editDriedAbaloneSummary.stockItemId = item.id;
        this.editDriedAbaloneSummary.farm = item.deliveryNote.farm.name;
        // eslint-disable-next-line max-len
        this.editDriedAbaloneSummary.receivingDate = this.formatDate(item.deliveryNote.receivingDate);
        // eslint-disable-next-line max-len
        this.editDriedAbaloneSummary.cookingDate = this.formatDate(dayjs(item.deliveryNote.receivingDate).add(2, 'day'));
        this.summaryItems = stockItemLinks.map((link) => ({
          sizeRange: `${link.childStockItem.sizeMin} - ${link.childStockItem.sizeMax}`,
        }));
        this.$log.info('Prepopulation completed');
        this.$forceUpdate();
        this.isLoading = false;
      } else {
        this.$log.info('No prepopulation required');
      }
    });
    this.isLoading = true;
    if (this.mode === 'edit') {
      this.$log.info('editing');
      await this.LOAD_driedAbaloneSummary(this.driedAbaloneSummaryId);
      this.editDriedAbaloneSummary = this.driedAbaloneSummary;
    }
    this.isLoading = false;
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('driedAbaloneSummaries', ['LOAD_driedAbaloneSummary', 'CREATE_driedAbaloneSummary', 'UPDATE_driedAbaloneSummary']),
    ...mapActions('driedAbaloneSummaryItems', ['LOAD_driedAbaloneSummaryItems', 'UPDATE_driedAbaloneSummaryItem']),
    ...mapActions('stockItemLinks', ['LOAD_stockItemLinksByParent']),
    /** Method used to save the item
     */
    async save({ localDriedAbaloneSummary, items }) {
      this.isSaving = true;
      if (this.mode === 'add') {
        const newItem = { ...localDriedAbaloneSummary };
        newItem.items = items;
        await this.CREATE_driedAbaloneSummary(newItem);
      } else {
        const updateDatedAbaloneCookingSheet = {
          ...localDriedAbaloneSummary,
        };
        updateDatedAbaloneCookingSheet.items = undefined;
        updateDatedAbaloneCookingSheet.rowVersion = undefined;
        await this.UPDATE_driedAbaloneSummary(updateDatedAbaloneCookingSheet);
        items.forEach(async (item) => {
          if (item.id) {
            await this.UPDATE_driedAbaloneSummaryItem(item);
          } else {
            const newItem = { ...item };
            newItem.driedAbaloneSummaryId = localDriedAbaloneSummary.id;
            await this.CREATE_driedAbaloneSummaryItem(newItem);
          }
        });
      }
      this.isSaving = false;
      this.$router.push({ name: 'processing-ListFactoryItem' });
    },
  },
};
</script>

<docs>
# EditDriedAbaloneSummary example

'''js
<EditDriedAbaloneSummary :DriedAbaloneSummary="DriedAbaloneSummary" />
'''
</docs>
